import { UseFormReturn } from "react-hook-form";
import { z, ZodSchema } from "zod";
import { GoogleAuthProvider, OAuthProvider, signOut } from "firebase/auth";
import { useMutation } from "@tanstack/react-query";
import { formatFirebaseError } from "@core/services/api/formatFirebaseError";
import { registerUserWithOAuth } from "../utils/registerUserWithOAuth";
import { authEventBus } from "../auth.events";
import { Invitation, ClaimedVia } from "@core/features/invitation";
import { initFirebase } from "@core/services/api/firebase";

type Args<T extends ZodSchema = ZodSchema> = {
  invitation: Invitation;
  claimedVia?: ClaimedVia | null;
  onError?: () => void;
  onSuccess?: (data: any) => void;
  schema: T;
  form: UseFormReturn<z.infer<T>>;
};

export function useGoogleSignupMutation<T extends ZodSchema>({
  invitation,
  claimedVia,
  onError,
  onSuccess,
  schema: _schema,
  form,
}: Args<T>) {

  return useMutation({
    mutationFn: async () => {
      return await registerUserWithOAuth(
        new GoogleAuthProvider() as OAuthProvider,
        invitation?.shortcode,
        claimedVia,
        onError
      );
    },
    onError: async (error: Error) => {
      form.setError("root", {
        type: "manual",
        message: formatFirebaseError(error.message),
      });

      if (error.message.indexOf("Email address already in use.") > -1) {
        const auth = await initFirebase();

        await signOut(auth);
      }

    },
    onSuccess: async (data) => {
      authEventBus.emitEvent("auth:signup:completed", {
        invitation,
      });

      // submit callback
      await onSuccess?.(data);
    },
  });
}

export default useGoogleSignupMutation;
